import FormEdit from "./steps/FormEdit.vue";
import { clone } from "@/util";
import { changedProjectFields } from "@/apps/office/util/project-changes";

import { Project, EditDialogAttributes } from "@/types";

import WithSteps from "@/apps/office/containers/table/shared/dialogs/with-steps";

type FormData = {
  item: Project;
  loaded: Project;
  formValid: boolean;
};

type DialogAttrs = EditDialogAttributes<Project> & { autofocus: string };

/**
 * A copy to edit, so a cancelled dialog leaves the page as it was. A project without a zonmaat
 * customer is a Wocozon project to the CRM (Project::zmCustomer()); every werkproject is one.
 * General.vue turns a missing conversion into 0 when it mounts. Both are applied here, to the copy
 * the diff starts from as well, so neither reads as an edit.
 */
const loadedProject = (item: Project): Project => {
  const copy = clone(item);

  if (copy.zmCustomer == null) copy.zmCustomer = "wocozon";
  if (copy.conversion == null) copy.conversion = 0;

  return copy;
};

export default WithSteps<FormData, DialogAttrs>({
  title: "office.dialogs.projects.edit.title",

  maxWidth: 600,

  formData() {
    return {
      item: loadedProject(this.$attrs.item),

      loaded: loadedProject(this.$attrs.item),

      formValid: false
    };
  },

  steps: [
    {
      component: FormEdit,

      isValid() {
        return this.$data.formValid;
      },

      props() {
        return {
          item: this.$data.item,
          autofocus: this.$attrs.autofocus,
          isEditing: true
        };
      },

      value: {
        get() {
          return this.$data.formValid;
        },

        set(isValid: boolean) {
          this.$data.formValid = isValid;
        }
      },

      labels: {
        action: "dialogs.save"
      },

      async afterStep() {
        // Only what changed: a save must not write back a field that another app or tab changed
        // after this page loaded.
        const changes = changedProjectFields(this.$data.loaded, this.$data.item);

        if (Object.keys(changes).length === 0) return;

        await this.$store.dispatch("projects/item:update", {
          id: this.$data.item.id,
          ...changes
        });

        await this.$store.dispatch(
          `projects/regular/fetch`,
          this.$data.item.id
        );
      }
    }
  ]
});
